import { CharacterRole } from '@/types/enums';
/**
 * IAM permissions — local copy for akiles-ecosystem (Vercel imports `@/integrity/iam/permissions`).
 *
 * Canonical: thegame/integrity/iam/permissions.ts — sync from there before deploy.
 */

export interface PermissionRule {
  resource: string;
  action: string;
  roles: readonly string[];
}

export interface AuthPermissions {
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: readonly string[]) => boolean;
  can: (resource: string, action: string) => boolean;
}

export const CHARACTERS_SPECIAL_ROLES = {
  FOUNDER: 'founder',
  PLAYER: 'player',
  APPRENTICE: 'apprentice',
  TEAM: 'team',
  FAMILY: 'family',
  TOKENHOLDER: 'token-holder',
  PARTNER: 'partner',
  AGENT: 'agent',
  CUSTOMER: 'customer',
  BENEFICIARY: 'beneficiary',
} as const;

// FOUNDER acts as the 'System Admin, God Rights Role'.
export const FOUNDER_ROLE = CHARACTERS_SPECIAL_ROLES.FOUNDER;

export const SPECIAL_ROLES = [
  CHARACTERS_SPECIAL_ROLES.FOUNDER,
  CHARACTERS_SPECIAL_ROLES.PLAYER,
  CHARACTERS_SPECIAL_ROLES.APPRENTICE,
  CHARACTERS_SPECIAL_ROLES.TEAM,
  CHARACTERS_SPECIAL_ROLES.FAMILY,
  CHARACTERS_SPECIAL_ROLES.TOKENHOLDER,
  CHARACTERS_SPECIAL_ROLES.PARTNER,
  CHARACTERS_SPECIAL_ROLES.AGENT,
  CHARACTERS_SPECIAL_ROLES.CUSTOMER,
  CHARACTERS_SPECIAL_ROLES.BENEFICIARY,
] as const;

// Front-end role groups used for visibility gates (site navigation + section access)
export const PLAYER_SELECTABLE_ROLES = [
  CHARACTERS_SPECIAL_ROLES.FOUNDER,
  CHARACTERS_SPECIAL_ROLES.TEAM,
  CHARACTERS_SPECIAL_ROLES.APPRENTICE,
] as const;

export const MARKET_ROLES = [
  CHARACTERS_SPECIAL_ROLES.CUSTOMER,
  CHARACTERS_SPECIAL_ROLES.BENEFICIARY,
] as const;


/**
 * PERMISSION_MATRIX — authorization keys for APIs / admin gates (`canAccess`, `createPermissionEvaluator().can`).
 *
 * NOT WIRED TO THE SYSTEM REALLY (YET)
 *
 * CRUD columns do not need to be symmetric: if nothing checks `delete`, omit it until it exists.
 * Role strings must match normalized JWT / character roles (lowercase).
 *
 * Sync matrix changes from thegame/integrity/iam/permissions.ts.
 */
export const PERMISSION_MATRIX: readonly PermissionRule[] = [
  { resource: 'account-dashboard', action: 'enter', roles: ['founder'] },                          // Enter Accounts Section: Only for Founder at the moment

] as const;

const VALID_ROLES = new Set<string>(Object.values(CharacterRole) as string[]);
for (const role of Object.values(CHARACTERS_SPECIAL_ROLES)) {
  VALID_ROLES.add(role);
}

export function normalizeRoles(rawRoles: unknown): string[] {
  if (!rawRoles) return [];
  const unique = new Set<string>();

  if (Array.isArray(rawRoles)) {
    for (const rawRole of rawRoles) {
      if (typeof rawRole !== 'string') continue;
      const normalized = rawRole.trim();
      if (!normalized || !VALID_ROLES.has(normalized)) continue;
      unique.add(normalized);
    }
    return Array.from(unique);
  }

  if (typeof rawRoles === 'string') {
    const normalized = rawRoles.trim();
    if (normalized && VALID_ROLES.has(normalized)) {
      unique.add(normalized);
    }
  }

  return Array.from(unique);
}

export function hasAnyRole(roles: unknown, requiredRoles: readonly string[]): boolean {
  const normalizedRoles = normalizeRoles(roles);
  const normalizedRequired = normalizeRoles(requiredRoles);
  return normalizedRequired.some((required) => normalizedRoles.includes(required));
}

function normalizeResourceAction(value: string): string {
  return value.trim().toLowerCase();
}

export function canAccess(
  roles: unknown,
  resource: string,
  action: string,
  permissionMatrix: readonly PermissionRule[] = PERMISSION_MATRIX,
): boolean {
  const normalizedRoles = normalizeRoles(roles);
  const normalizedResource = normalizeResourceAction(resource);
  const normalizedAction = normalizeResourceAction(action);

  const matching = permissionMatrix.filter(
    (permission) =>
      normalizeResourceAction(permission.resource) === normalizedResource &&
      normalizeResourceAction(permission.action) === normalizedAction,
  );

  return matching.some((permission) =>
    permission.roles.some((permissionRole) => {
      const normalizedRole = permissionRole.trim();
      return normalizedRoles.includes(normalizedRole);
    }),
  );
}

export function isGameAdmin(roles: unknown): boolean {
  return hasAnyRole(roles, [FOUNDER_ROLE]);
}

export function isFounder(roles: unknown): boolean {
  return hasAnyRole(roles, [CHARACTERS_SPECIAL_ROLES.FOUNDER]);
}

export function isTeam(roles: unknown): boolean {
  return hasAnyRole(roles, [CHARACTERS_SPECIAL_ROLES.TEAM]);
}

export function isApprentice(roles: unknown): boolean {
  return hasAnyRole(roles, [CHARACTERS_SPECIAL_ROLES.APPRENTICE]);
}

export function isCustomer(roles: unknown): boolean {
  return hasAnyRole(roles, [CHARACTERS_SPECIAL_ROLES.CUSTOMER]);
}

export function isPlayer(roles: unknown): boolean {
  return hasAnyRole(roles, [CHARACTERS_SPECIAL_ROLES.PLAYER]);
}

export function isPartner(roles: unknown): boolean {
  return hasAnyRole(roles, [CHARACTERS_SPECIAL_ROLES.PARTNER]);
}

export function isSpecialRole(roles: unknown): boolean {
  return hasAnyRole(roles, SPECIAL_ROLES);
}

export function isPlayerSelectable(roles: unknown): boolean {
  return hasAnyRole(roles, PLAYER_SELECTABLE_ROLES);
}

export function isCustomerOrBeneficiary(roles: unknown): boolean {
  return hasAnyRole(roles, MARKET_ROLES);
}

export function canAccessProjects(roles: unknown): boolean {
  if (isGameAdmin(roles)) return true;
  return isPlayerSelectable(roles) && isCustomerOrBeneficiary(roles);
}

export function createPermissionEvaluator(
  rawRoles: unknown,
  permissionMatrix: readonly PermissionRule[] = PERMISSION_MATRIX,
): AuthPermissions {
  const normalizedRoles = normalizeRoles(rawRoles);
  const hasAdminRight = isGameAdmin(normalizedRoles);

  return {
    hasRole: (role: string) => {
      if (hasAdminRight) return true;
      const normalizedRole = typeof role === 'string' ? role.trim() : '';
      if (!normalizedRole || !VALID_ROLES.has(normalizedRole)) return false;
      return normalizedRoles.includes(normalizedRole);
    },
    hasAnyRole: (roles: readonly string[]) => {
      if (hasAdminRight) return true;
      return normalizeRoles(roles).some((role) => normalizedRoles.includes(role));
    },
    can: (resource: string, action: string) => {
      if (hasAdminRight) return true;
      return canAccess(normalizedRoles, resource, action, permissionMatrix);
    },
  };
}
